import moment, { Moment, isMoment, unitOfTime } from 'moment';
import { getPaddedDisplayNumber } from '@/helpers/string';

interface Time {
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  months: number;
  years: number;
}

export type WeekRange = {
  startDate: string;
  endDate: string;
};
export type ElapsedTime = Readonly<Time>;
export type CountdownTime = Readonly<Time>;

export const MONTHS: { [key: number]: string } = {
  0: 'Janeiro',
  1: 'Fevereiro',
  2: 'Março',
  3: 'Abril',
  4: 'Maio',
  5: 'Junho',
  6: 'Julho',
  7: 'Agosto',
  8: 'Setembro',
  9: 'Outubro',
  10: 'Novembro',
  11: 'Dezembro',
};

export const WEEK_DAYS_NAMES = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];

export const ONE_HOUR_IN_TIMESTAMP = 3600000;

export const SHORT_MONTHS: { [key: number]: string } = {
  0: 'Jan',
  1: 'Fev',
  2: 'Mar',
  3: 'Abr',
  4: 'Mai',
  5: 'Jun',
  6: 'Jul',
  7: 'Ago',
  8: 'Set',
  9: 'Out',
  10: 'Nov',
  11: 'Dez',
};

export const getDiffTime = (fromTime: Moment | string, toTime: Moment | string): ElapsedTime => {
  const from = isMoment(fromTime) ? fromTime : moment(fromTime);
  const to = isMoment(toTime) ? toTime : moment(toTime);

  return {
    seconds: from.diff(to, 'seconds'),
    minutes: from.diff(to, 'minutes'),
    hours: from.diff(to, 'hours'),
    days: from.diff(to, 'days'),
    months: from.diff(to, 'months'),
    years: from.diff(to, 'years'),
  };
};

export const getElapsedTime = (originTime: string): ElapsedTime => {
  const currentMoment: Moment = moment();
  const originMoment: Moment = moment(originTime);

  return getDiffTime(currentMoment, originMoment);
};

export const getCountdownTime = (originTime: string): CountdownTime => {
  const currentMoment: Moment = moment();
  const originMoment: Moment = moment(originTime);

  return getDiffTime(originMoment, currentMoment);
};

export const timeSinceCreation = (createdAt: string, isShort: boolean = false): string => {
  const { minutes, hours, days } = getElapsedTime(createdAt);

  if (days > 2) {
    const date = new Date(createdAt);
    if (isShort) {
      return `${date.getDate()} ${
        SHORT_MONTHS[date.getMonth()]
      }. ${date.getFullYear()} • ${getPaddedDisplayNumber(
        date.getHours(),
      )}:${getPaddedDisplayNumber(date.getMinutes())}`;
    }
    return `${date.getDate()} de ${MONTHS[date.getMonth()]} de ${date.getFullYear()}`;
  }

  if (days > 0 && days <= 2) {
    const str = `há ${days} dia`;
    return days === 1 ? str : `${str}s`;
  }

  if (hours > 0 && hours < 24) {
    const str = `há ${hours} hora`;
    return hours === 1 ? str : `${str}s`;
  }

  if (minutes === 0) return 'agora';

  const str = `há ${minutes} minuto`;
  return minutes === 1 ? str : `${str}s`;
};

export const secondsToTimestamp = (seconds: number): string => {
  if (seconds >= 3600) return moment.utc(seconds * 1000).format('HH:mm:ss');
  return moment.utc(seconds * 1000).format('mm:ss');
};

export const isToday = (dateToCompare: string): boolean => {
  const today = moment().utc(true);
  const date = moment.utc(dateToCompare);

  return today.startOf('day').isSame(date.startOf('day'));
};

export const isGreaterThan = (dateToCompare: string, dateToBeGreater: string): boolean => {
  return moment.utc(dateToCompare).isAfter(moment.utc(dateToBeGreater));
};

export const isSmallerThan = (dateToCompare: string, dateToBeSmaller: string): boolean => {
  return moment.utc(dateToCompare).isBefore(moment.utc(dateToBeSmaller));
};

export const formatDate = (date: Date, format: string, utc = true): string => {
  return utc ? moment.utc(date).format(format) : moment(date).format(format);
};
export const getWeekRangeByDate = (date: Date): WeekRange => {
  const weekStart = moment.utc(date).startOf('week');
  const weekEnd = moment.utc(date).endOf('week');

  return {
    startDate: weekStart.toISOString(),
    endDate: weekEnd.toISOString(),
  } as WeekRange;
};

export const startOf = (date: Date, unit: unitOfTime.StartOf = 'day'): Date => {
  return moment.utc(date).startOf(unit).toDate();
};

export const isBetween = (
  dateToCompare: string,
  dateToBeGreater: string,
  dateToBeSmaller: string,
): boolean => {
  return (
    isGreaterThan(dateToCompare, dateToBeGreater) && isSmallerThan(dateToCompare, dateToBeSmaller)
  );
};

export const getDateWithoutMinutes = (date: Date): Date => {
  date.setMinutes(0, 0, 0);
  return date;
};

export const getNearestDate = (dates: string[]): string | null => {
  let nearestDate: string = '';

  dates.forEach((date) => {
    const diff = moment(date).diff(moment(), 'seconds');

    if (diff > 0) {
      if (nearestDate) {
        if (moment(date).diff(moment(nearestDate), 'seconds') < 0) {
          nearestDate = date;
        }
      } else {
        nearestDate = date;
      }
    }
  });

  return nearestDate || null;
};

export const differenceInMonths = (begin: Date, end: Date) => {
  const monthDiff = end.getMonth() - begin.getMonth();
  const yearDiff = end.getFullYear() - begin.getFullYear();

  return monthDiff + yearDiff * 12;
};
